import type { SupportedLocale } from "@/i18n";

/** Locale suffixes used on legacy CMS cover positions (homepageFR, aboutEN, …). */
export const POSITION_LANG_SUFFIXES = ["FR", "EN", "ES", "NL"] as const;

export type PositionLangSuffix = (typeof POSITION_LANG_SUFFIXES)[number];

const DEFAULT_WEBSITE_LANGUAGES: readonly SupportedLocale[] = [
  "fr",
  "en",
  "es",
  "nl",
];

const LOCALE_TO_SUFFIX: Record<SupportedLocale, PositionLangSuffix> = {
  fr: "FR",
  en: "EN",
  es: "ES",
  nl: "NL",
};

const SUFFIX_TO_LOCALE: Record<PositionLangSuffix, SupportedLocale> = {
  FR: "fr",
  EN: "en",
  ES: "es",
  NL: "nl",
};

const POSITION_LANG_SUFFIX_RE = /^(.*)(FR|EN|ES|NL)$/;

export function parseCmsLanguageImagePosition(position: string): {
  prefix: string;
  locale: SupportedLocale;
  suffix: PositionLangSuffix;
} | null {
  const match = position.match(POSITION_LANG_SUFFIX_RE);
  if (!match) return null;
  const [, prefix, suffix] = match as [string, string, PositionLangSuffix];
  return {
    prefix,
    suffix,
    locale: SUFFIX_TO_LOCALE[suffix],
  };
}

/**
 * Sibling CMS cover positions for other website languages.
 * Returns [] when `position` is not language-suffixed (newsImage, découpage, …).
 */
export function getSiblingCmsLanguageImagePositions(
  position: string,
  websiteLanguages: readonly SupportedLocale[] = DEFAULT_WEBSITE_LANGUAGES,
): string[] {
  return getSiblingCmsLanguageImageTargets(position, websiteLanguages).map(
    (t) => t.position,
  );
}

export type CmsLanguageImageTarget = {
  locale: SupportedLocale;
  position: string;
};

/** Sibling targets with locale for UI (flags / selection). */
export function getSiblingCmsLanguageImageTargets(
  position: string,
  websiteLanguages: readonly SupportedLocale[] = DEFAULT_WEBSITE_LANGUAGES,
): CmsLanguageImageTarget[] {
  const parsed = parseCmsLanguageImagePosition(position);
  if (!parsed) return [];

  return websiteLanguages
    .filter((lang) => lang !== parsed.locale)
    .map((lang) => ({
      locale: lang,
      position: `${parsed.prefix}${LOCALE_TO_SUFFIX[lang]}`,
    }));
}

/** Uppercase locale labels for dialog copy (FR, EN, …). */
export function formatCmsLanguageLabels(positions: string[]): string[] {
  return positions
    .map((p) => parseCmsLanguageImagePosition(p)?.suffix)
    .filter((s): s is PositionLangSuffix => Boolean(s));
}
